import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Select, Tag, Space, Tabs, Input, message, Button} from 'antd';
import { UserOutlined, ReloadOutlined } from '@ant-design/icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import thLocale from '@fullcalendar/core/locales/th'; 
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import CustomModal from './CustomModal';
import moment from 'moment-timezone';
import { confirmAlert } from 'react-confirm-alert'; // Import the confirmation dialog
import 'react-confirm-alert/src/react-confirm-alert.css'; // Import the styles
import 'antd/dist/reset.css';
import './Classes.css';
import { jwtDecode } from "jwt-decode";
import BranchDetailsModal from './BranchDetailsModal';

const { Option } = Select;
const { TabPane } = Tabs;

function BranchCalendar() {
  const { t, i18n } = useTranslation();
  const [classes, setClasses] = useState([]);
   const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [teacherEvents, setTeacherEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [, setClassrooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentEventInfo, setCurrentEventInfo] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [branches, setBranches] = useState([]);
  const [calendarView, setCalendarView] = useState('timeGridDay');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [filters, setFilters] = useState({
    branch: '',  // ✅ new filter for branch
    });
  const calendarRef = useRef(null);
    const token = localStorage.getItem("token");
    let userRole = null;
    let userBranchId = null;

    if (token) {
    try {
        const decoded = jwtDecode(token);
        userRole = decoded.role;
        // Support both formats just in case
        userBranchId = decoded.branchId || decoded.branch_id || null;
    } catch (err) {
        console.error("Invalid token", err);
    }
    }

  const API_URL = process.env.REACT_APP_API_URL;

   const refreshCalendarData = async () => {
  try {
    setLoading(true);

    const calendarApi = calendarRef.current?.getApi();
    const currentView = calendarApi?.view?.type;
    const currentDate = calendarApi?.getDate();

    // ✅ Get the active date range
    const view = calendarApi?.view;
    const startDate = moment(view.currentStart).format("YYYY-MM-DD");
    const endDate = moment(view.currentEnd).format("YYYY-MM-DD");

    // ✅ Fetch with correct range
    await Promise.all([
      fetchClasses(startDate, endDate),
      fetchClassChangeLogs()
    ]);

    if (selectedTeachers.length > 0) {
      await fetchTeacherEvents(selectedTeachers);
    }

    // Restore current view
    setTimeout(() => {
      if (calendarApi && currentView && currentDate) {
        calendarApi.changeView(currentView, currentDate);
        calendarApi.refetchEvents?.();
      }
    }, 100);

  } catch (error) {
    console.error("❌ Error refreshing calendar data:", error);
    message.error("Failed to refresh calendar data");
  } finally {
    setLoading(false);
  }
};

  const parseScheduleHour = (scheduleHourString) => {
    const [hours, minutes] = scheduleHourString.split(' ').map((value, index) => (index === 0 ? parseInt(value) * 60 : parseInt(value)));
    return hours + minutes;
  };

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const teacher = teachers.find(t => t.userid === value);
    
    return (
      <Tag
        color={teacher?.color || '#108ee9'}
        closable={closable}
        onClose={onClose}
        style={{ 
          marginRight: 3,
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
        }}
      >
        <Space size={4}>
          <UserOutlined style={{ fontSize: '12px' }} />
          {label}
        </Space>
      </Tag>
    );
  };

  useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };

  fetchBranches();
}, [API_URL]);

const fetchClasses = useCallback(
  async (startDate, endDate) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No token found");
        setLoading(false);
        return;
      }

      const decoded = jwtDecode(token);
      const role = decoded.role;
      const branchId = decoded.branchId || decoded.branch_id || null;

      const params = { startDate, endDate };
      if (role === "admin" && branchId) {
        params.branchId = branchId;
      }

      

      const response = await axios.get(`${API_URL}/classes/branch-classes`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setClasses(response.data?.data || []);
    } catch (error) {
      console.error("❌ Error fetching classes:", error);
      setError("Failed to fetch classes. Please ensure you are logged in.");
    } finally {
      setLoading(false);
    }
  },
  [API_URL]
);



 const fetchClassChangeLogs = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Properly call the setter and update the state
    setClassChangeLogs(response.data);

  } catch (error) {
    console.error('❌ Error fetching class change logs:', error);
  }
}, [API_URL]);
 
 const fetchTeacherEvents = useCallback(async (teacherIds) => {
    if (teacherIds.length === 0) {
      setTeacherEvents([]);
      return;
    }
  
    try {
      const token = localStorage.getItem('token');
      const promises = teacherIds.map((teacherId) =>
        axios.get(`${API_URL}/teachers/calendar/${teacherId}/events-with-shared`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(response => ({ 
          teacherId,  // Include teacherId with the response
          data: response.data 
        }))
      );
  
      const results = await Promise.all(promises);
      
      // Format events once when fetching
      const formattedEvents = results.flatMap((result) => 
        result.data.map(event => {
          const teacher = teachers.find(t => t.userid === result.teacherId); // Use the passed teacherId
          const isShared = event.is_shared || false;
  
          return {
            ...event,
            id: `event-${event.eventid}`,
            start: moment.utc(event.start).local().format(),
            end: moment.utc(event.end).local().format(),
            color: isShared ? '#005082' : (teacher ? teacher.color : '#444444'),
            backgroundColor: isShared 
              ? '#00508260' 
              : (teacher ? `${teacher.color}60` : '#44444480'),
            borderColor: isShared ? '#005082' : (teacher ? teacher.color : '#444444'),
            classNames: ['teacher-schedule-event']
          };
        })
      );
  
      setTeacherEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching teacher events:', error);
      setError('Failed to fetch events. Please try again later.');
    }
  }, [API_URL, teachers]);
  
  useEffect(() => {
    fetchClasses();
    fetchClassChangeLogs();
    setTeacherEvents([]); 
  }, [fetchClasses, fetchClassChangeLogs, fetchTeacherEvents]);
  

useEffect(() => {
  const fetchTeachers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/teachers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const teachersWithColors = (response.data.teachers || []).map((teacher) => ({
        ...teacher,
        color: teacher.color || '#000000',
      }));

      setTeachers(teachersWithColors);

    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  fetchTeachers();
}, [API_URL]);


  

  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/classrooms`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
  
        const classroomsData = response.data || [];
  
        // Filter classrooms with availability 'true'
        const availableClassrooms = classroomsData.filter(classroom => classroom.availability === true);
  
        // Sort by 'number' field in alphanumeric order
        const sortedClassrooms = availableClassrooms.sort((a, b) => {
          return a.number.localeCompare(b.number, undefined, { numeric: true, sensitivity: 'base' });
        });
  
        setClassrooms(sortedClassrooms);
      } catch (error) {
        console.error('Error fetching classrooms:', error);
      }
    };
  
    fetchClassrooms();
  }, [API_URL]);
  

  useEffect(() => {
    if (selectedTeachers.length > 0) {
      fetchTeacherEvents(selectedTeachers);
    } else {
      setTeacherEvents([]);
    }
  }, [selectedTeachers, fetchTeacherEvents]);



  const checkOverlap = (newStart, newEnd, teacherId, eventId, allClasses, teacherEvents) => {
    return [...allClasses, ...teacherEvents].some(event => {
      // Skip the current event being moved/resized
      if (event.classid?.toString() === eventId || event.eventid?.toString() === eventId) return false;
      
      // Check all events for the same teacher
      if (event.teacher_id !== teacherId && event.userid !== teacherId) return false;
  
      const eventStart = moment(event.start || `${event.date} ${event.schedule_time}`);
      const eventEnd = moment(event.end || `${event.date} ${event.end_time}`);
  
      return (
        newStart.isBetween(eventStart, eventEnd, null, '[)') ||
        newEnd.isBetween(eventStart, eventEnd, null, '(]') ||
        (newStart.isSameOrBefore(eventStart) && newEnd.isSameOrAfter(eventEnd))
      );
    });
  };

  const handleEventDrop = (info) => {
    const eventId = info.event.id;
    const classItem = classes.find(cls => cls.classid.toString() === eventId);
  
    if (classItem && classItem.status === 'approved') {
      confirmAlert({
        title: t('Action Not Allowed'),
        message: t('You cannot change an approved class.'),
        buttons: [{ label: t('ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    const newStart = moment(info.event.start);
    const newEnd = moment(info.event.end);
    const teacherId = classItem?.teacher_id;
  
    const isOverlapping = checkOverlap(newStart, newEnd, teacherId, eventId, classes, teacherEvents);
  
    if (isOverlapping) {
      confirmAlert({
        title: t('Time Slot Conflict'),
        message: t('The selected time overlaps with an existing class or event for the same teacher. Please choose a different time.'),
        buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    setCurrentEventInfo(info);
    setShowModal(true);
  };
  
  
const handleEventResize = async (info) => {
  const eventId = info.event.id;
  const classItem = classes.find(cls => cls.classid.toString() === eventId);

  if (!classItem) {
    console.warn('Class not found in local state');
    info.revert();
    return;
  }

  if (classItem.status === 'approved') {
    confirmAlert({
      title: t('Action Not Allowed'),
      message: t('You cannot change an approved class.'),
      buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
    });
    return;
  }

  const newStart = moment(info.event.start);
  const newEnd = moment(info.event.end);
  const teacherId = classItem.teacher_id;

  const isOverlapping = checkOverlap(newStart, newEnd, teacherId, eventId, classes, teacherEvents);
  if (isOverlapping) {
    confirmAlert({
      title: t('Time Slot Conflict'),
      message: t('The selected time overlaps with an existing class or event for the same teacher. Please choose a different time.'),
      buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
    });
    return;
  }

  try {
    const token = localStorage.getItem('token');

    // Fetch group usage info
    const groupRes = await axios.get(`${API_URL}/class-groups/by-groupid/${classItem.groupid}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const apiData = groupRes.data;
    const allowedMinutes = parseInt(apiData.total_hours, 10) || 0;
    const usedMinutes = parseInt(apiData.used_hours || apiData.used_minutes, 10) || 0;

    // ✅ FIX: Parse the schedule_hour string correctly
    function parseScheduleHour(scheduleHourStr) {
      if (!scheduleHourStr) return 0;
      
      // Handle formats like "2h 0m", "1h 30m", "45m", etc.
      const hourMatch = scheduleHourStr.match(/(\d+)h/);
      const minuteMatch = scheduleHourStr.match(/(\d+)m/);
      
      const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
      const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
      
      return (hours * 60) + minutes;
    }

    const currentDuration = parseScheduleHour(classItem.schedule_hour);
    const newDuration = newEnd.diff(newStart, 'minutes');
    const newTotalUsed = usedMinutes - currentDuration + newDuration;

    if (newTotalUsed > allowedMinutes) {
      const remainingMinutes = allowedMinutes - (usedMinutes - currentDuration);
      
      confirmAlert({
        title: t('Exceeds Total Hours'),
        message: t(
          `This resize would exceed the allowed total time for this course.\n\n` +
          `Total Allowed: ${Math.floor(allowedMinutes / 60)}:${(allowedMinutes % 60).toString().padStart(2, '0')}\n hrs \n` +
          `Exceeds by: ${Math.floor((newTotalUsed - allowedMinutes) / 60)}:${((newTotalUsed - allowedMinutes) % 60).toString().padStart(2, '0')} mins`
        ),
        buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
      });
      return;
    }

    setCurrentEventInfo(info);
    setShowModal(true);

  } catch (error) {
    console.error('Error during resize validation:', error);
    info.revert();
  }
};

 const handleSubmitModal = async () => {
  const { event } = currentEventInfo;
  const classItem = classes.find(cls => cls.classid.toString() === event.id);

  // Store current calendar state before update
  const calendarApi = calendarRef.current?.getApi();
  const currentView = calendarApi?.view.type;
  const currentDate = calendarApi?.getDate();

  const view = calendarApi?.view;
  const startDate = moment(view.currentStart).format("YYYY-MM-DD");
  const endDate = moment(view.currentEnd).format("YYYY-MM-DD");

  // Define original and new times
  const originalStartTime = classItem.schedule_time; 
  const originalEndTime = classItem.end_time; 
  const newStartTime = event.start.toTimeString().split(' ')[0].substring(0, 5);
  const newEndTime = event.end.toTimeString().split(' ')[0].substring(0, 5);

  // Format dates with moment-timezone
  const originalDate = moment.tz(classItem.date, 'Asia/Bangkok').format('YYYY-MM-DD');
  const newDate = moment.tz(event.start, 'Asia/Bangkok').format('YYYY-MM-DD');

  const updatedEvent = {
      id: event.id,
      date: newDate,
      schedule_time: newStartTime,
      schedule_hour: ((event.end - event.start) / 60000).toFixed(0)
  };

  try {
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/classes/${updatedEvent.id}`, updatedEvent, {
          headers: {
              Authorization: `Bearer ${token}`
          }
      });

      await axios.post(`${API_URL}/class-change/class-change-logs`, {
          classid: updatedEvent.id,
          teacherid: classItem.teacher_id,
          originaldate: originalDate,
          originalstarttime: originalStartTime,
          originalendtime: originalEndTime,
          newdate: newDate,
          newstarttime: newStartTime,
          newendtime: newEndTime,
          changereason: changeReason,
          note: changeNote,
          updateby: token
      }, {
          headers: {
              Authorization: `Bearer ${token}`
          }
      });

      // Close modal first
      setShowModal(false);
      setCurrentEventInfo(null);
      setChangeReason('');
      setChangeNote('');
      
      // Refresh classes data
      await fetchClasses(startDate, endDate);
      await fetchClassChangeLogs();
    
    // Refresh teacher events if any are selected
      if (selectedTeachers.length > 0) {
        await fetchTeacherEvents(selectedTeachers);
      }
      
      // Restore calendar view after data is loaded
      setTimeout(() => {
        if (calendarApi && currentView && currentDate) {
          calendarApi.changeView(currentView, currentDate);
        }
      }, 100);
      
  } catch (error) {
    console.error('Error updating event:', error);
    setShowModal(false);
    setCurrentEventInfo(null);
  }
};

  const handleEventClick = (info) => {
    const eventId = info.event.id;
    
    // For class events (not teacher events)
    if (!eventId.includes('event-')) {
      const classItem = classes.find(cls => cls.classid.toString() === eventId);
      if (classItem) {
        const teacher = teachers.find(t => t.userid === classItem.teacher_id);
        setSelectedClass({
          classData: classItem,
          teacherData: teacher
        });
        setDetailsModalVisible(true);
      }
    } else {
      // For teacher events - you can handle these differently or ignore them
      const eventIdNum = eventId.replace('event-', '');
      const teacherEvent = teacherEvents.find(evt => evt.eventid?.toString() === eventIdNum);
      if (teacherEvent) {

      }
    }
  };

  const handleFilterChange = (key, value) => {
  setFilters(prev => ({ ...prev, [key]: value }));
};

  
const filteredClasses = classes.filter((classItem) => {
  const isTeacherMatch =
    selectedTeachers.length > 0
      ? selectedTeachers.includes(classItem.teacher_id)
      : true;

  const isSearchMatch =
    searchTerm === '' ||
    classItem.class_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    classItem.subject_name?.toLowerCase().includes(searchTerm.toLowerCase());

  // ✅ New branch filter
  const isBranchMatch =
    !filters.branch || String(classItem.branch_id) === String(filters.branch);

  return isTeacherMatch && isSearchMatch && isBranchMatch;
});



const formatClassToEvent = (classItem) => {
  const startDate = new Date(classItem.date);
  const [startHours, startMinutes] = classItem.schedule_time.split(':').map(Number);

  if (isNaN(startHours) || isNaN(startMinutes)) {
      console.error('Invalid schedule time format:', classItem.schedule_time);
      return null;
  }

  startDate.setHours(startHours);
  startDate.setMinutes(startMinutes);
  startDate.setSeconds(0);
  startDate.setMilliseconds(0);

  if (isNaN(startDate.getTime())) {
      console.error('Invalid start date or time:', classItem.date, classItem.schedule_time, startDate);
      return null;
  }

  const totalMinutes = parseScheduleHour(classItem.schedule_hour || '');
  const endDateTime = new Date(startDate.getTime() + totalMinutes * 60000);

  if (isNaN(endDateTime.getTime())) {
      console.error('Invalid end date or time:', startDate, endDateTime, classItem.schedule_hour);
      return null;
  }

  const teacher = teachers.find((t) => t.userid === classItem.teacher_id);

  // Get platform icon and text
  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'online':
        return `💻 ${t('online')}`;
      case 'onsite':
        return `📍 ${t('onsite')}`;
      case 'athome':
        return `🏠 ${t('athome')}`;
      default:
        return `❓ ${platform}`;
    }
  };

      const getClassTypeLabel = (isPrivate) => {
      return isPrivate ? `👤 ${t('private')}` : `👥 ${t('group')}`;
    };


  // Remove platform from nickname line
  const titleLines = [
    `${classItem.class_code}`,
    `${classItem.subject_name} (${classItem.classid})`,
    `${classItem.nickname}`, // Platform removed from here
  ];

  return {
      id: classItem.classid.toString(),  
      title: titleLines.join('<br />'), 
      start: startDate.toISOString(), 
      end: endDateTime.toISOString(),
      color: teacher ? teacher.color : '#333333', 
      backgroundColor: teacher ? teacher.color : '#333333',
      extendedProps: {
        platform: getPlatformIcon(classItem.platform),

        branchName: classItem.name || 'Unknown Branch', // Branch name from API
        student_confirm: classItem.student_confirm === true || classItem.student_confirm === 'true',
      }
  };
};

const renderEventContent = (arg) => {
  const event = arg.event;
  const startTime = event.start ? moment(event.start).format('HH:mm') : '';
  const endTime = event.end ? moment(event.end).format('HH:mm') : '';
  const platform = event.extendedProps?.platform || '';
  const branchName = event.extendedProps?.branchName || '';
  const confirmed = event.extendedProps?.student_confirm;

  // Choose icon based on confirmed state
  const confirmIcon = confirmed
    ? `✅` // check mark
    : `❌`; // cross mark

  return {
    html: `
     <div class="calendar-event teacher-event" style="
          padding: 4px;
          border-radius: 4px;
          min-height: 100%;
          font-size: 1em;
          position: relative;
        ">
          <div style="margin-bottom: 2px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
            <span>${startTime} - ${endTime} ${confirmIcon}</span>
          </div>
          <div style="margin-bottom: 20px; ">
            ${event.title}
          </div>
          <div class="platform-tag" style="
            position: absolute;
            bottom: 2px;
            left: 2px;
            background: rgb(255, 255, 255);
            padding: 2px 4px;
            border-radius: 3px;
            font-size: 0.9em;
            color: #333;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          ">
            ${platform} 🏢 ${branchName}
          </div>
        </div>
    `,
  };
};
const calendarEvents = [
  ...filteredClasses.map(formatClassToEvent).filter((event) => event !== null),
  ...teacherEvents // Use directly since they're already formatted
];

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;



  return (
    <div className="class-container">
      <Sidebar />
      <Header />
      <div className="top-filter-container">
      <div className="teacher-select-container">
{userRole === "superadmin" && (
  <Select
    placeholder={t("All Branches")}
    value={filters.branch || undefined}
    onChange={(value) => handleFilterChange("branch", value || "")}
    allowClear
    style={{ width: 300 }}
  >
    {branches.map((branch) => (
      <Option key={branch.id} value={branch.id}>
        {branch.name}
      </Option>
    ))}
  </Select>
)}


        <div >
            <Input.Search
              placeholder={t('Search Course or Subject Name')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '350px' }}
              allowClear
            />
          </div>
                <Select
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            placeholder={t('Select Teachers')}
            value={selectedTeachers}
            onChange={setSelectedTeachers}
            optionFilterProp="label" // Changed from children to label
            tagRender={tagRender}
            maxTagCount={3}
            maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} ${t('more teachers')}`}
            filterOption={(input, option) => {
              // Check if the search term matches nickname or firstname
              return option?.label?.toLowerCase().includes(input.toLowerCase());
            }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          >
            {teachers.map(teacher => (
              <Option 
                key={teacher.userid} 
                value={teacher.userid}
                label={`${teacher.nickname}`} // Add label prop for search
                style={{
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Space>
                  <Tag color={teacher.color} style={{ margin: 0, width: 8, height: 8 }} />
                  <span>{teacher.nickname}</span>
                </Space>
              </Option>
            ))}
          </Select>

<Button
      type="primary"
      icon={<ReloadOutlined />}
      onClick={refreshCalendarData}
      loading={loading}
      style={{ minWidth: '120px' }}
    >
      {t('Refresh')}
    </Button>


      </div>
      </div>

    <div className="class-calendar-container">
      <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={calendarView}
          initialDate={calendarDate}
          allDaySlot={false}
          locale={i18n.language === 'th' ? thLocale : 'en'}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          slotMinTime="09:00:00"
          slotMaxTime="20:00:00"
          slotDuration="00:15:00"
          scrollTime="09:00:00" 
          snapDuration="00:15:00" 
          slotLabelInterval="00:30" 
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false, 
            }}
          events={calendarEvents}
          editable={true}
          droppable={true}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventClick={handleEventClick}
          eventDisplay="block"
          eventBackgroundColor="transparent"
          eventContent={renderEventContent}
          eventClassNames={(info) => info.event.extendedProps.classNames}
          dayMaxEvents={0}
          height="100%"
          datesSet={(dateInfo) => {
            const viewType = dateInfo.view.type;

            // Use FullCalendar’s provided range
            const start = dateInfo.startStr.split("T")[0]; // YYYY-MM-DD
            const end = dateInfo.endStr.split("T")[0];     // YYYY-MM-DD

            const targetDate = dateInfo.view.calendar.getDate();
            const targetDateStr = targetDate.toLocaleDateString();
            const currentDateStr = calendarDate ? calendarDate.toLocaleDateString() : '';

            if (targetDateStr !== currentDateStr || calendarView !== viewType) {
                setCalendarDate(targetDate);
                setCalendarView(viewType);

                // ✅ Fetch using the exact range instead of a single date
                fetchClasses(start, end);
            }
            }}


          views={{
            dayGridMonth: {
              displayEventTime: true,
              displayEventEnd: true,
              dayMaxEvents: false
            },
            timeGridWeek: {
              displayEventTime: true,
              displayEventEnd: true
            },
            timeGridDay: {
              displayEventTime: true,
              displayEventEnd: true
            }
          }}
        />
    </div>
  <BranchDetailsModal
        visible={detailsModalVisible}
        onClose={() => {
          setDetailsModalVisible(false);
          setSelectedClass(null);
        }}
        classData={selectedClass?.classData}
        teacherData={selectedClass?.teacherData}
        classChangeLogs={classChangeLogs}
        onRefresh={refreshCalendarData} // Pass the refresh function here
      />
    
            <CustomModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              onSubmit={handleSubmitModal}
              reason={changeReason}
              setReason={setChangeReason}
              note={changeNote}
              setNote={setChangeNote}
            />
          </div>
          
  );
  
}

export default withRoleAccess(BranchCalendar, ['admin', 'superadmin']);